import type { CommentImages } from './comment-images';
import type { SeedingComment } from './pick-comments';

/**
 * Комментарий картинкой — так же, как его приносили руками.
 *
 * В образце отчёта комментарии стоят настоящими снимками ленты Instagram:
 * Артур делал их с телефона и вставлял в документ. Просьба «нам нужно
 * комментарии прям скринами… чтобы точно видно было что с инсты» — про этот
 * вид, а не про способ получения. Повторить его текстом в таблице Word нельзя:
 * шрифт, серый низ и сердце справа — это и есть признак, по которому читатель
 * узнаёт приложение.
 *
 * Поэтому карточка рисуется на canvas по данным Graph API: те же строки в том
 * же порядке, что в приложении. Аватарка — стандартный серый силуэт Instagram:
 * фотографий чужих профилей Graph API не отдаёт (в `from` приходят только id и
 * ник), и Артур на заглушку согласился.
 *
 * Картинка одна на оба выхода: в Word она уходит как ImageRun, в Google Docs —
 * через публичную ссылку, потому что insertInlineImage не принимает data:.
 *
 * Комментарии TikTok рисуются в другой вид — тёмной темой приложения, как
 * скриншоты в образце «2026.09.16 топ видео»: имя профиля, текст, дата и
 * «Ответить» снизу, сердце со счётом справа, «Просмотреть N ответов». Шрифт —
 * TikTok Sans, аватарка настоящая, фирменные эмодзи («[Лайк]») — картинками,
 * фото и стикер — под текстом. Всё это готовит comment-images.
 */

/** Логическая ширина карточки в точках; рисуем вдвое крупнее и ужимаем. */
export const CARD_WIDTH = 340;
const SCALE = 2;

const PAD_X = 12;
const PAD_Y = 10;
const AVATAR = 32;
const GAP = 10;

const COLORS = {
  bg: '#ffffff',
  name: '#262626',
  text: '#262626',
  meta: '#8e8e8e',
  avatar: '#dbdbdb',
  avatarInk: '#ffffff',
  divider: '#efefef',
};

const FONT = '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const NAME_FONT = `600 13px ${FONT}`;
const TEXT_FONT = `13px ${FONT}`;
const META_FONT = `12px ${FONT}`;

/**
 * Перенос по словам под заданную ширину.
 *
 * Отдельно от рисования, чтобы проверять тестом: длинное слово без пробелов
 * (ссылка, «!!!!!!!!») не должно уезжать за край карточки, а обычный текст —
 * рваться посреди слова.
 */
export function wrapText(text: string, maxWidth: number, measure: (s: string) => number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const candidate = line ? `${line} ${word}` : word;
      if (measure(candidate) <= maxWidth || !line) {
        // Слово шире карточки целиком — режем по символам, иначе строка вылезет.
        if (!line && measure(word) > maxWidth) {
          let part = '';
          for (const ch of word) {
            if (measure(part + ch) > maxWidth && part) {
              lines.push(part);
              part = ch;
            } else {
              part += ch;
            }
          }
          line = part;
          continue;
        }
        line = candidate;
        continue;
      }
      lines.push(line);
      line = word;
    }
    lines.push(line);
  }
  return lines.filter((l, i) => l !== '' || i === 0);
}

/** «Отметки „Нравится": 5» — подпись как в приложении. */
function metaLine(comment: SeedingComment): string {
  const parts = [`Отметки «Нравится»: ${comment.likeCount.toLocaleString('ru-RU')}`, 'Ответить'];
  return parts.join('   ');
}

function drawAvatar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  colors: { avatar: string; avatarInk: string } = COLORS,
): void {
  const r = size / 2;
  ctx.fillStyle = colors.avatar;
  ctx.beginPath();
  ctx.arc(x + r, y + r, r, 0, Math.PI * 2);
  ctx.fill();
  // Силуэт: голова и плечи — то, что Instagram показывает вместо фотографии.
  ctx.fillStyle = colors.avatarInk;
  ctx.beginPath();
  ctx.arc(x + r, y + r * 0.78, r * 0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x + r, y + r * 1.62, r * 0.52, Math.PI, 0);
  ctx.fill();
}

function drawHeart(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string = COLORS.meta,
): void {
  const s = size / 24;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(12, 21);
  ctx.bezierCurveTo(4, 15.5, 1, 12, 1, 8.5);
  ctx.bezierCurveTo(1, 5.4, 3.4, 3, 6.5, 3);
  ctx.bezierCurveTo(9, 3, 11, 4.6, 12, 6.3);
  ctx.bezierCurveTo(13, 4.6, 15, 3, 17.5, 3);
  ctx.bezierCurveTo(20.6, 3, 23, 5.4, 23, 8.5);
  ctx.bezierCurveTo(23, 12, 20, 15.5, 12, 21);
  ctx.stroke();
  ctx.restore();
}

export type CommentCardImage = { dataUrl: string; width: number; height: number };

/**
 * Карточки списка комментариев по id. Сбой на одном не роняет выгрузку: этот
 * комментарий просто уйдёт текстом.
 */
export function drawCommentCards(
  comments: SeedingComment[],
  images: Map<string, CommentImages> = new Map(),
): Map<string, CommentCardImage> {
  const cards = new Map<string, CommentCardImage>();
  for (const comment of comments) {
    try {
      cards.set(comment.id, renderCommentCard(comment, images.get(comment.id)));
    } catch (err) {
      console.warn('[Комментарии] не нарисовался:', comment.id, err);
    }
  }
  return cards;
}

/**
 * Одна карточка комментария. Возвращает PNG data-URL и его размер в точках —
 * размер нужен обоим потребителям, чтобы вставить картинку без искажения.
 */
export function renderCommentCard(comment: SeedingComment, images: CommentImages = {}): CommentCardImage {
  if (comment.platform === 'tiktok') return renderTikTokCard(comment, images);
  const textLeft = PAD_X + AVATAR + GAP;
  const textWidth = CARD_WIDTH - textLeft - PAD_X - 24; // 24 — место под сердце

  // Меряем на временном холсте: высота карточки зависит от числа строк.
  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) throw new Error('Не удалось нарисовать комментарий');
  probe.font = TEXT_FONT;
  const lines = wrapText(comment.text, textWidth, (s) => probe.measureText(s).width);

  const headH = 18;
  const lineH = 18;
  const metaH = 18;
  const height = Math.max(PAD_Y * 2 + AVATAR, PAD_Y * 2 + headH + lines.length * lineH + metaH);

  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Не удалось нарисовать комментарий');
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, CARD_WIDTH, height);

  drawAvatar(ctx, PAD_X, PAD_Y, AVATAR);

  ctx.textBaseline = 'alphabetic';
  let y = PAD_Y + 13;

  ctx.font = NAME_FONT;
  ctx.fillStyle = COLORS.name;
  ctx.fillText(comment.username, textLeft, y);
  const nameWidth = ctx.measureText(comment.username).width;

  if (comment.date) {
    ctx.font = META_FONT;
    ctx.fillStyle = COLORS.meta;
    ctx.fillText(comment.date, textLeft + nameWidth + 8, y);
  }

  ctx.font = TEXT_FONT;
  ctx.fillStyle = COLORS.text;
  for (const line of lines) {
    y += lineH;
    ctx.fillText(line, textLeft, y);
  }

  y += metaH;
  ctx.font = META_FONT;
  ctx.fillStyle = COLORS.meta;
  ctx.fillText(metaLine(comment), textLeft, y);

  drawHeart(ctx, CARD_WIDTH - PAD_X - 18, PAD_Y + 4, 16);

  ctx.strokeStyle = COLORS.divider;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, height - 0.5);
  ctx.lineTo(CARD_WIDTH, height - 0.5);
  ctx.stroke();

  return { dataUrl: canvas.toDataURL('image/png'), width: CARD_WIDTH, height };
}

// Тёмная тема TikTok. Цвета, отступы и шаг строк сняты со скриншотов образца
// (451 px в ширину → 340 точек): фон чисто чёрный, текст #f6f6f6, подписи #999.
const TT_FONT = `"TikTok Sans", ${FONT}`;
const TT = {
  padX: 6,
  avatar: 28,
  gap: 8,
  heartW: 30,
  bg: '#000000',
  text: '#f6f6f6',
  meta: '#999999',
  avatarBg: '#2f2f2f',
  avatarInk: '#8a8a8a',
  nameFont: `600 13px ${TT_FONT}`,
  textFont: `14px ${TT_FONT}`,
  metaFont: `11px ${TT_FONT}`,
  replyFont: `600 11px ${TT_FONT}`,
  nameBaseline: 21,
  firstLine: 44,
  lineH: 19,
  metaGap: 22,
  padBottom: 18,
  /** Эмодзи TikTok в строке — чуть крупнее букв, как в приложении. */
  emoji: 17,
  /** Фото и стикер: стикер квадратный, фото вписывается в рамку. */
  sticker: 84,
  photoW: 150,
  photoH: 150,
  attachGap: 8,
  repliesGap: 26,
};

/** Кусок строки: текст или фирменное эмодзи картинкой. */
export type RichToken = { kind: 'text'; text: string } | { kind: 'emoji'; id: string; fallback: string };

/**
 * Текст с фирменными эмодзи → куски по порядку. Эмодзи без картинки остаётся
 * подписью «[Лайк]», как его видит сайт без каталога.
 */
export function richTokens(comment: SeedingComment, hasImage: (id: string) => boolean): RichToken[] {
  const tokens: RichToken[] = [];
  let at = 0;
  const emojis = [...(comment.emojis ?? [])].sort((a, b) => a.start - b.start);
  for (const e of emojis) {
    if (e.start < at) continue;
    if (e.start > at) tokens.push({ kind: 'text', text: comment.text.slice(at, e.start) });
    const fallback = comment.text.slice(e.start, e.end);
    tokens.push(hasImage(e.id) ? { kind: 'emoji', id: e.id, fallback } : { kind: 'text', text: fallback });
    at = e.end;
  }
  if (at < comment.text.length) tokens.push({ kind: 'text', text: comment.text.slice(at) });
  return tokens;
}

/**
 * Перенос строк с эмодзи-картинками. Слово — неразрывный кусок между
 * пробелами; эмодзи, прилепленное к слову, переносится вместе с ним, как в
 * приложении. Слово шире строки режется по символам (см. wrapText).
 */
export function wrapRich(
  tokens: RichToken[],
  maxWidth: number,
  measure: (s: string) => number,
  emojiWidth: number,
): RichToken[][] {
  type Piece = RichToken & { width: number };
  // Слова: массивы кусков; разделители — пробелы и переводы строк.
  const words: Array<Piece[] | 'space' | 'newline'> = [];
  let word: Piece[] = [];
  const flush = () => {
    if (word.length) words.push(word);
    word = [];
  };
  for (const t of tokens) {
    if (t.kind === 'emoji') {
      word.push({ ...t, width: emojiWidth });
      continue;
    }
    for (const part of t.text.split(/(\s)/)) {
      if (part === '\n') {
        flush();
        words.push('newline');
      } else if (/^\s$/.test(part)) {
        flush();
        words.push('space');
      } else if (part) {
        word.push({ kind: 'text', text: part, width: measure(part) });
      }
    }
  }
  flush();

  const lines: Piece[][] = [[]];
  let width = 0;
  const spaceW = measure(' ');
  const line = () => lines[lines.length - 1];
  const pushPiece = (p: Piece) => {
    const last = line()[line().length - 1];
    if (last && last.kind === 'text' && p.kind === 'text') {
      line()[line().length - 1] = { ...last, text: last.text + p.text, width: last.width + p.width };
    } else {
      line().push(p);
    }
    width += p.width;
  };
  const newLine = () => {
    lines.push([]);
    width = 0;
  };

  let pendingSpace = false;
  for (const w of words) {
    if (w === 'newline') {
      newLine();
      pendingSpace = false;
      continue;
    }
    if (w === 'space') {
      pendingSpace = line().length > 0;
      continue;
    }
    const wordW = w.reduce((n, p) => n + p.width, 0);
    const gap = pendingSpace ? spaceW : 0;
    if (line().length > 0 && width + gap + wordW > maxWidth) {
      newLine();
    } else if (gap) {
      pushPiece({ kind: 'text', text: ' ', width: spaceW });
    }
    pendingSpace = false;
    for (const p of w) {
      if (p.kind === 'text' && p.width > maxWidth - width) {
        // Слово не влезает и в пустую строку — режем по символам.
        for (const ch of p.text) {
          const chW = measure(ch);
          if (width + chW > maxWidth && line().length > 0) newLine();
          pushPiece({ kind: 'text', text: ch, width: chW });
        }
        continue;
      }
      if (width + p.width > maxWidth && line().length > 0 && p.kind === 'emoji') newLine();
      pushPiece(p);
    }
  }
  return lines
    .map((l) => l.map(({ width: _w, ...t }) => t as RichToken))
    .filter((l, i, all) => l.length > 0 || (i === 0 && all.length === 1));
}

/** «Просмотреть 1 ответ / 3 ответа / 5 ответов». */
function repliesLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word =
    mod10 === 1 && mod100 !== 11 ? 'ответ' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'ответа' : 'ответов';
  return `Просмотреть ${n} ${word}`;
}

function drawAvatarImage(ctx: CanvasRenderingContext2D, image: CanvasImageSource, x: number, y: number, size: number): void {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(image, x, y, size, size);
  ctx.restore();
}

function imageSize(image: CanvasImageSource): { width: number; height: number } {
  const i = image as { width?: number; height?: number };
  return { width: i.width || 1, height: i.height || 1 };
}

/** Размер вложения в карточке: стикер — квадрат, фото — вписано в рамку. */
function attachmentBox(comment: SeedingComment, image: CanvasImageSource): { width: number; height: number } {
  const { width, height } = imageSize(image);
  if (!comment.imageUrl) {
    const k = TT.sticker / Math.max(width, height);
    return { width: width * k, height: height * k };
  }
  const k = Math.min(TT.photoW / width, TT.photoH / height);
  return { width: width * k, height: height * k };
}

function renderTikTokCard(comment: SeedingComment, images: CommentImages): CommentCardImage {
  const textLeft = TT.padX + TT.avatar + TT.gap;
  const textWidth = CARD_WIDTH - textLeft - TT.padX - TT.heartW;
  const name = comment.nickname || comment.username;
  const emojiImages = images.emojis ?? new Map<string, CanvasImageSource>();

  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) throw new Error('Не удалось нарисовать комментарий');
  probe.font = TT.textFont;
  const tokens = richTokens(comment, (id) => emojiImages.has(id));
  const lines = wrapRich(tokens, textWidth, (s) => probe.measureText(s).width, TT.emoji + 1);

  const attachment = images.attachment ? attachmentBox(comment, images.attachment) : null;
  const lastLine = TT.firstLine + (lines.length - 1) * TT.lineH;
  const attachTop = lastLine + TT.attachGap;
  const metaY = (attachment ? attachTop + attachment.height + 6 : lastLine) + TT.metaGap;
  const repliesY = comment.replyCount ? metaY + TT.repliesGap : null;
  const height = (repliesY ?? metaY) + TT.padBottom;

  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Не удалось нарисовать комментарий');
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = TT.bg;
  ctx.fillRect(0, 0, CARD_WIDTH, height);

  const avatarY = TT.nameBaseline - 13;
  if (images.avatar) drawAvatarImage(ctx, images.avatar, TT.padX, avatarY, TT.avatar);
  else drawAvatar(ctx, TT.padX, avatarY, TT.avatar, { avatar: TT.avatarBg, avatarInk: TT.avatarInk });

  ctx.textBaseline = 'alphabetic';
  ctx.font = TT.nameFont;
  ctx.fillStyle = TT.text;
  ctx.fillText(name, textLeft, TT.nameBaseline, CARD_WIDTH - textLeft - TT.padX);

  ctx.font = TT.textFont;
  lines.forEach((line, i) => {
    const y = TT.firstLine + i * TT.lineH;
    let x = textLeft;
    for (const t of line) {
      if (t.kind === 'emoji') {
        const image = emojiImages.get(t.id);
        if (image) ctx.drawImage(image, x, y - TT.emoji + 3, TT.emoji, TT.emoji);
        x += TT.emoji + 1;
      } else {
        ctx.fillText(t.text, x, y);
        x += ctx.measureText(t.text).width;
      }
    }
  });

  if (images.attachment && attachment) {
    ctx.save();
    if (comment.imageUrl) {
      // Фото в приложении — со скруглёнными углами.
      ctx.beginPath();
      ctx.roundRect(textLeft, attachTop, attachment.width, attachment.height, 6);
      ctx.clip();
    }
    ctx.drawImage(images.attachment, textLeft, attachTop, attachment.width, attachment.height);
    ctx.restore();
  }

  ctx.fillStyle = TT.meta;
  let x = textLeft;
  if (comment.date) {
    ctx.font = TT.metaFont;
    ctx.fillText(comment.date, x, metaY);
    x += ctx.measureText(comment.date).width + 16;
  }
  ctx.font = TT.replyFont;
  ctx.fillText('Ответить', x, metaY);

  // Сердце со счётом — справа на строке даты, счёт прижат к краю.
  ctx.font = TT.metaFont;
  const likes = String(comment.likeCount);
  const likesW = ctx.measureText(likes).width;
  ctx.fillText(likes, CARD_WIDTH - TT.padX - likesW, metaY);
  drawHeart(ctx, CARD_WIDTH - TT.padX - likesW - 18, metaY - 11, 14, TT.meta);

  if (repliesY !== null && comment.replyCount) {
    // «—— Просмотреть 1 ответ ⌄» — как под комментарием в приложении.
    ctx.strokeStyle = '#4a4a4a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(textLeft, repliesY - 4);
    ctx.lineTo(textLeft + 24, repliesY - 4);
    ctx.stroke();
    ctx.font = TT.replyFont;
    const label = repliesLabel(comment.replyCount);
    ctx.fillText(label, textLeft + 32, repliesY);
    const cx = textLeft + 32 + ctx.measureText(label).width + 8;
    ctx.strokeStyle = TT.meta;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(cx, repliesY - 7);
    ctx.lineTo(cx + 4, repliesY - 3);
    ctx.lineTo(cx + 8, repliesY - 7);
    ctx.stroke();
  }

  return { dataUrl: canvas.toDataURL('image/png'), width: CARD_WIDTH, height };
}
